import { useCallback, useEffect, useState } from "react";

import { formatInt, formatUsd } from "@kontextwerk/web-common";

import CostSummaryCard from "../components/CostSummaryCard";
import CostTrendChart from "../components/CostTrendChart";
import FilterBar from "../components/FilterBar";
import PendingCostNotice from "../components/PendingCostNotice";
import LayerPieChart from "../components/LayerPieChart";
import StackedBreakdownChart from "../components/StackedBreakdownChart";
import { fetchCostSummary, fetchCostTimeseries } from "../lib/api";
import { defaultRange, type DateRange } from "../lib/dateRange";
import type {
  CostFilters,
  CostSummaryResponse,
  CostTimeseriesResponse,
} from "../lib/types";

export default function OverviewRoute() {
  const [range, setRange] = useState<DateRange>(defaultRange(30));
  const [filters, setFilters] = useState<CostFilters>({});
  const [includeTodayLive, setIncludeTodayLive] = useState(true);
  const [timeseries, setTimeseries] = useState<CostTimeseriesResponse | null>(
    null,
  );
  const [layerBreakdown, setLayerBreakdown] = useState<CostSummaryResponse | null>(
    null,
  );
  const [opClassBreakdown, setOpClassBreakdown] = useState<CostSummaryResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ts, layer, ops] = await Promise.all([
        fetchCostTimeseries(range.from, range.to, "day", filters, includeTodayLive),
        fetchCostSummary(range.from, range.to, "layer", filters, includeTodayLive),
        fetchCostSummary(range.from, range.to, "op_class", filters, includeTodayLive),
      ]);
      setTimeseries(ts);
      setLayerBreakdown(layer);
      setOpClassBreakdown(ops);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, filters, includeTodayLive]);

  useEffect(() => {
    load();
  }, [load]);

  const totalCost = timeseries?.buckets.reduce(
    (acc, b) => acc + b.total_cost_usd,
    0,
  ) ?? 0;
  const totalCalls = timeseries?.buckets.reduce(
    (acc, b) => acc + b.total_calls,
    0,
  ) ?? 0;
  const avgCostPerCall = totalCalls > 0 ? totalCost / totalCalls : 0;

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-medium">Overview</h1>
        <span className="text-xs text-kw-text-faint">
          {range.from} → {range.to}
        </span>
      </header>
      <FilterBar
        range={range}
        onRangeChange={setRange}
        filters={filters}
        onFiltersChange={setFilters}
        showLayerFilter
        includeTodayLive={includeTodayLive}
        onIncludeTodayLiveChange={setIncludeTodayLive}
      />

      {error ? (
        <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">
          {error}
        </div>
      ) : null}

      <PendingCostNotice pending={layerBreakdown?.pending} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CostSummaryCard
          label="Summe (USD)"
          value={formatUsd(totalCost)}
          sub={`${formatInt(totalCalls)} Calls insgesamt`}
        />
        <CostSummaryCard
          label="Calls"
          value={formatInt(totalCalls)}
          sub={`Buckets: ${timeseries?.buckets.length ?? 0}`}
        />
        <CostSummaryCard
          label="Ø Cost/Call"
          value={formatUsd(avgCostPerCall)}
          sub="USD je gemessenem LLM-Aufruf"
        />
      </div>

      <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded p-4">
        <h2 className="text-sm font-medium text-kw-text-muted mb-2">
          Tages-Trend (USD)
        </h2>
        {loading && !timeseries ? (
          <p className="text-sm text-kw-text-faint">Lade…</p>
        ) : (
          <CostTrendChart buckets={timeseries?.buckets ?? []} />
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded p-4">
          <h2 className="text-sm font-medium text-kw-text-muted mb-2">
            Layer-Anteil (L1/L2/L3/L4)
          </h2>
          <LayerPieChart buckets={layerBreakdown?.buckets ?? []} />
        </section>
        <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded p-4">
          <h2 className="text-sm font-medium text-kw-text-muted mb-2">
            Top op_class
          </h2>
          <StackedBreakdownChart buckets={opClassBreakdown?.buckets ?? []} />
        </section>
      </div>
    </div>
  );
}
